import { index, modelOptions, mongoose, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { ObjectIdString, StoredVerdiIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../zod-decorator/index.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { AccessGrantFieldsWithDefaults, AccessGrantSchemaTypes } from "../types/accessGrant.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "accessGrants" as const;

export enum AccessGrantSubjectType {
    USER = "USER",
}

export enum AccessGrantRole {
    ORGANIZATION_ADMIN = "ORGANIZATION_ADMIN",
}

/** Exactly one role for now. */
const AccessGrantRoles = z.array(z.enum(AccessGrantRole)).length(1);

/** Roles a subject holds in an organization, or in one of its sites. SCD Type-2: removing access is a logical delete. */
@index(
    { subjectType: 1, subjectId: 1, organizationId: 1, siteId: 1 },
    { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } },
)
@index({ organizationId: 1 }, { partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@index({ subjectId: 1 }, { partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "accessGrants" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<AccessGrant>>(...addSchemaKeyHook(SCHEMA_KEY))
export class AccessGrant extends ScdDocument<AccessGrant, AccessGrantFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.enum(AccessGrantSubjectType) })
    @prop({ type: () => String, required: true, enum: AccessGrantSubjectType })
    public subjectType!: AccessGrantSubjectType;

    /** The subject's verdiId: a user account's for USER. */
    @validate({ zodValidator: StoredVerdiIdString })
    @prop({ type: () => String, required: true, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public subjectId!: string;

    /** The organization's verdiId. */
    @validate({ zodValidator: StoredVerdiIdString })
    @prop({ type: () => String, required: true, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public organizationId!: string;

    /** The site's verdiId; absent grants the whole organization. */
    @validate({ zodValidator: StoredVerdiIdString.optional() })
    @prop({ type: () => String, required: false, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public siteId?: string;

    @validate({ zodValidator: AccessGrantRoles })
    @prop(
        {
            type: () => [String],
            required: true,
            enum: AccessGrantRole,
            validate: mongooseValidatorFromZod(AccessGrantRoles),
        },
        PropType.ARRAY,
    )
    public roles!: AccessGrantRole[];

    static getters = {} as const satisfies Record<string, (self: AccessGrant) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<AccessGrantSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<AccessGrant, AccessGrantFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

AccessGrant.dtoValidators = getZodSchemaFromClass(AccessGrant) as unknown as DtoValidatorTypes<AccessGrantSchemaTypes>;
